import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnInit,
  afterRenderEffect,
  computed,
  forwardRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

export interface SearchSelectOption {
  value: number | string;
  label: string;
}

export type SearchSelectFilterMode = 'remote' | 'client';

export type SearchSelectToOption<T = any> = (value: T | null) => SearchSelectOption | null;
export type SearchSelectFromOption<T = any> = (option: SearchSelectOption | null) => T | null;

let nextComponentId = 0;

@Component({
  selector: 'mh-core-lib-search-select-input',
  templateUrl: './search-select-input.component.html',
  styleUrls: ['./search-select-input.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CoreLibSearchSelectInputComponent),
      multi: true,
    },
  ],
})
export class CoreLibSearchSelectInputComponent implements OnInit, ControlValueAccessor {
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly textChanges$ = new Subject<string>();

  readonly componentId = nextComponentId++;

  options = input<SearchSelectOption[]>([]);
  selectedOption = input<SearchSelectOption | null>(null);
  placeholder = input('');
  disabledInput = input(false, { alias: 'disabled' });
  loading = input(false);
  minChars = input(3);
  debounceMs = input(300);
  noResultsText = input('');
  customClassInput = input('');
  filterMode = input<SearchSelectFilterMode>('remote');
  toOption = input<SearchSelectToOption>((value) => value as SearchSelectOption | null);
  fromOption = input<SearchSelectFromOption>((option) => option);

  searchTextChange = output<string>();
  loadRequested = output<void>();
  cleared = output<void>();
  selected = output<SearchSelectOption>();

  selection = linkedSignal(() => this.selectedOption());

  private disabledByForm = signal(false);
  isDisabled = computed(() => this.disabledInput() || this.disabledByForm());

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  filterValue = signal('');
  openBox = signal(false);

  displayedOptions = computed(() => {
    if (this.filterMode() !== 'client') {
      return this.options();
    }
    const text = this.filterValue().trim().toLowerCase();
    if (!text) {
      return this.options();
    }
    return this.options().filter((option) => option.label.toLowerCase().includes(text));
  });

  private hasEnoughChars = computed(
    () => this.filterMode() !== 'remote' || this.filterValue().trim().length >= this.minChars(),
  );

  noResultsVisible = computed(
    () => this.openBox() && !this.loading() && this.hasEnoughChars() && !this.displayedOptions().length,
  );

  markedIndex = linkedSignal<SearchSelectOption[], number>({
    source: () => this.displayedOptions(),
    computation: (options) => {
      if (!this.openBox() || !options.length) {
        return -1;
      }
      const index = options.findIndex((option) => option.value === this.selection()?.value);
      return index >= 0 ? index : 0;
    },
  });

  constructor() {
    afterRenderEffect(() => {
      if (this.markedIndex() < 0) {
        return;
      }
      const marked = this.elementRef.nativeElement.querySelector('.search-select-option-marked');
      marked?.scrollIntoView({ block: 'nearest' });
    });
  }

  ngOnInit(): void {
    this.textChanges$
      .pipe(
        debounceTime(this.debounceMs()),
        filter((text) => this.filterMode() === 'remote' && text.length >= this.minChars()),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((text) => this.searchTextChange.emit(text));
  }

  writeValue(value: any): void {
    this.selection.set(this.toOption()(value));
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByForm.set(isDisabled);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closeAndClearSearch();
    }
  }

  onInput(event: Event) {
    const value = (event.target as HTMLInputElement).value ?? '';
    this.filterValue.set(value);
    this.openBox.set(true);
    this.textChanges$.next(value);
  }

  onKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveMarked(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveMarked(-1);
        break;
      case 'Enter': {
        const option = this.displayedOptions()[this.markedIndex()];
        if (this.openBox() && option) {
          event.preventDefault();
          this.selectOption(option);
        }
        break;
      }
      case 'Escape':
        this.closeAndClearSearch();
        break;
    }
  }

  handleFocus() {
    if (this.isDisabled()) {
      return;
    }
    this.openBox.set(true);
    if (this.filterMode() === 'client' && !this.options().length && !this.loading()) {
      this.loadRequested.emit();
    }
  }

  handleBlur() {
    this.closeAndClearSearch();
    this.onTouched();
  }

  onOptionMouseDown(event: MouseEvent) {
    event.preventDefault();
  }

  selectOption(option: SearchSelectOption) {
    this.selection.set(option);
    this.closeAndClearSearch();
    this.onChange(this.fromOption()(option));
    this.onTouched();
    this.selected.emit(option);
  }

  clearSelection(event: MouseEvent) {
    event.stopPropagation();
    this.selection.set(null);
    this.closeAndClearSearch();
    this.onChange(this.fromOption()(null));
    this.onTouched();
    this.cleared.emit();
  }

  private closeAndClearSearch() {
    this.filterValue.set('');
    this.openBox.set(false);
    this.markedIndex.set(-1);
  }

  private moveMarked(offset: number) {
    if (!this.openBox()) {
      this.handleFocus();
      return;
    }
    const total = this.displayedOptions().length;
    if (!total) {
      return;
    }
    const next = (this.markedIndex() + offset + total) % total;
    this.markedIndex.set(next);
  }
}
